import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { X, Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import configs from '../config/config';
// ─── Stable debounce factory ─────────────────────────────────────
const createDebounce = (func, wait) => {
  let timeout;
  const debounced = (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
  debounced.cancel = () => clearTimeout(timeout);
  return debounced;
};

const CartSection = ({ isOpen, onClose, config = {} }) => {
  const { cartItems, isLoading, syncError, changeQuantity, removeItem } = useCart();
  const navigate = useNavigate();
   let featuredBg = localStorage.getItem('featuredBg') || configs.BASE_COLOR;
  const activeBg = localStorage.getItem('activeBg') || configs.BASE_COLOR;
if(featuredBg=='bg-gray-400'){
  featuredBg=configs.BASE_COLOR;
}
  const actualConfig = config?.cartSettings || config;

  const defaultConfig = {
    activeDesign: 'sidebar',
    title: 'Your Cart',
    emptyMessage: 'Your cart is empty.',
    continueShoppingText: 'Continue Shopping',
    checkoutText: 'Proceed to Checkout',
    customStyles: {
      background: featuredBg,
      textColor: 'text-white',
      buttonBg: featuredBg,
      buttonText: 'text-white',
    },
  };

  const finalConfig = { ...defaultConfig, ...actualConfig };
  const { activeDesign, title, emptyMessage, continueShoppingText, checkoutText, customStyles } =
    finalConfig;

  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }, []);

  const subtotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0),
    [cartItems]
  );

  const CartItemRow = React.memo(
    ({ item, navigate, changeQuantity, removeItem, customStyles, formatCurrency }) => {
      const [localQty, setLocalQty] = useState(item.quantity || 1);
      const [isDirty, setIsDirty] = useState(false);

      // Sync local state when external quantity changes
      useEffect(() => {
        setLocalQty(item.quantity || 1);
        setIsDirty(false);
      }, [item.quantity]);

     const debouncedSave = useMemo(
  () =>
    createDebounce((newQuantity) => {
      const qty = Number(newQuantity);
      if (!isNaN(qty) && qty >= 1 && qty !== item.quantity) {
        // Keep delta or switch to absolute as needed
        changeQuantity(item.product_id, qty - item.quantity);
        // changeQuantity(item.product_id, qty); // ← use this if absolute
      }
      setIsDirty(false);
    }, 600),
  [item.product_id, item.quantity, changeQuantity] // dependencies still needed
);

      // Cleanup debounce on unmount / item change
      useEffect(() => {
        return () => {
          debouncedSave.cancel();
        };
      }, [debouncedSave]);

      const handleChange = (e) => {
        const val = e.target.value.trim();

        // Allow empty string while typing + only positive integers
        if (val === '' || /^\d+$/.test(val)) {
          const numVal = val === '' ? '' : Number(val);
          setLocalQty(numVal);
          setIsDirty(true);

          // If valid number → schedule debounced save
          if (val !== '' && Number(val) >= 1) {
            debouncedSave(Number(val));
          }
        }
      };

      const handleBlur = () => {
        if (!isDirty) return;

        const qty = Number(localQty);

        if (isNaN(qty) || qty < 1 || localQty === '') {
          // Revert to server value if invalid
          setLocalQty(item.quantity || 1);
        } else if (qty !== item.quantity) {
          // Final save on blur if still different
          changeQuantity(item.product_id, qty - item.quantity);
          // or: changeQuantity(item.product_id, qty); // if absolute
        }

        setIsDirty(false);
      };

      const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur(); // triggers handleBlur → save or revert
        }
      };

      return (
        <div
          className="flex gap-4 pb-6 border-b last:border-0 cursor-pointer"
          onClick={() => navigate(`/products/${item.product_id}`)}
        >
          <div className="w-[100px] h-[80px] rounded-xl overflow-hidden bg-gray-100">
  <img
    src={item.image || 'https://placehold.co/96x96?text=No+Image'}
    alt={item.name}
    className="w-full h-full"
    loading="lazy"
    decoding="async"
  />
</div>
          <div className="flex-1">
            <h3 className={`font-medium mb-1 text-gray-900`}>{item.name}</h3>
            <p className="text-md font-bold text-gray-700 mb-2">
              {formatCurrency((item.price || 0) * (item.quantity || 1))} 
              {item.hasVariants &&(
              <span className="bg-green-600 text-white ml-4 rounded p-1">{item.countVariants} variant(s)</span>
              )}
            </p>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-gray-100 rounded-full px-3 py-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    changeQuantity(item.product_id, -1);
                  }}
                  disabled={item.quantity <= 1}
                  className="disabled:opacity-40 text-gray-900"
                  aria-label="Decrease quantity"
                >
                  <Minus size={16} />
                </button>

                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={localQty === '' ? '' : localQty}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  onClick={(e) => e.stopPropagation()}
                  className="w-16 md:w-16 text-center font-bold bg-transparent 
                           focus:bg-white focus:ring-2 focus:ring-blue-500 
                           rounded-md border border-gray-900 focus:border-blue-500 
                           outline-none transition-all"
                  min="1"
                />

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    changeQuantity(item.product_id, 1);
                  }}
                  className="text-gray-900"
                  aria-label="Increase quantity"
                >
                  <Plus size={16} />
                </button>
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  removeItem(item.product_id);
                }}
                className="text-red-600 hover:text-red-700 text-sm flex items-center gap-1"
                aria-label="Remove item"
              >
                <Trash2 size={14} /> Remove
              </button>
            </div>
          </div>
        </div>
      );
    }
  );

  if (!isOpen) return null;

  // ─── Sidebar Design ────────────────────────────────────────────────
  const DesignSidebar = () => (
    <div
      className={`fixed inset-y-0 right-0 w-full md:w-96 shadow-2xl z-[99999999] flex flex-col transform transition-transform bg-white duration-300 ease-out ${customStyles.background}`}
    >
      <div className={`p-6 border-b flex justify-between items-center ${customStyles.textColor}`}>
        <h2 className="text-2xl font-semibold">
          {title} ({cartItems.length})
        </h2>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" aria-label="Close cart">
          <X className="w-6 h-6 text-gray-600" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {isLoading && cartItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
            <p className="mt-4 text-gray-600">Loading your cart...</p>
          </div>
        ) : (
          <>
            {isLoading && (
              <div className="text-center text-sm text-gray-500 py-2">Updating cart...</div>
            )}
            {cartItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <ShoppingBag className="w-16 h-16 text-gray-300 mb-4" />
                <p className="text-gray-600">{emptyMessage}</p>
                <button
                  onClick={() => navigate('/Shop/all')}
                  className={`w-full py-4 rounded-xl font-semibold hover:opacity-90 transition  ${customStyles.buttonText}`} style={{backgroundColor:featuredBg}}
                >
                  Start shopping
                </button>
              </div>
            ) : (
              cartItems.map((item) => (
                <CartItemRow
                  key={item.product_id}
                  item={item}
                  navigate={navigate}
                  changeQuantity={changeQuantity}
                  removeItem={removeItem}
                  customStyles={customStyles}
                  formatCurrency={formatCurrency}
                />
              ))
            )}
          </>
        )}
      </div>

      {cartItems.length > 0 && (
        <div className="p-6 border-t bg-gray-50">
          <div className={`flex justify-between text-lg font-medium mb-4 ${customStyles.textColor}`}>
            <span>Subtotal</span>
            <span>{formatCurrency(subtotal)}</span>
          </div>
          {syncError && <div className="text-red-600 text-sm mb-3">{syncError}</div>}
          <button
            onClick={() => navigate('/Checkout')}
            className={`w-full py-4 rounded-xl font-semibold hover:opacity-90 transition  ${customStyles.buttonText}`} style={{backgroundColor:featuredBg || config.BASE_COLOR}}
          >
            {checkoutText} 
          </button>
          <button
            onClick={() => navigate('/shop/all')}
            className={`w-full mt-3 text-gray-900 hover:underline text-center block`}
          >
            {continueShoppingText}
          </button>
        </div>
      )}
    </div>
  );

  // ─── Bottom Sheet ──────────────────────────────────────────────────
  const DesignBottomSheet = () => (
    <div
      className="fixed inset-0 bg-black/50 z-[9999] flex items-end"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`w-full rounded-t-3xl shadow-2xl max-h-[90vh] flex flex-col transform transition-transform duration-300 ${customStyles.background} ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pt-4 pb-2">
          <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
        </div>

        <div className={`px-6 pb-4 border-b ${customStyles.textColor}`}>
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold">
              {title} ({cartItems.length})
            </h2>
            <button onClick={onClose} aria-label="Close cart">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {isLoading && cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-gray-900"></div>
              <p className="mt-4 text-gray-600">Loading cart...</p>
            </div>
          ) : (
            <>
              {isLoading && <div className="text-center text-sm text-gray-500 py-2">Updating...</div>}
              {cartItems.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingBag className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600">{emptyMessage}</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {cartItems.map((item) => (
                    <CartItemRow
                      key={item.product_id}
                      item={item}
                      navigate={navigate}
                      changeQuantity={changeQuantity}
                      removeItem={removeItem}
                      customStyles={customStyles}
                      formatCurrency={formatCurrency}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {cartItems.length > 0 && (
          <div className="p-6 border-t bg-gray-50">
            <div className={`flex justify-between text-xl font-bold mb-4 ${customStyles.textColor}`}>
              <span>Total</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            {syncError && <div className="text-red-600 text-sm mb-3">{syncError}</div>}
            <button
              onClick={() => navigate('/Checkout')}
              className={`w-full py-4 rounded-2xl font-medium hover:opacity-90 ${customStyles.buttonText}`} style={{backgroundColor:featuredBg}}
            >
              {checkoutText}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // ─── Center Modal ──────────────────────────────────────────────────
  const DesignCenterModal = () => (
    <div className="fixed inset-0 bg-black/70 z-[9999] flex items-center justify-center p-4">
      <div
        className={`rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col ${customStyles.background}`}
        role="dialog"
        aria-modal="true"
      >
        <div className={`p-6 border-b flex justify-between items-center ${customStyles.textColor}`}>
          <h2 className="text-2xl font-bold">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" aria-label="Close">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {isLoading && cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
              <p className="mt-4 text-gray-600">Loading your cart...</p>
            </div>
          ) : (
            <>
              {isLoading && <div className="text-center text-sm text-gray-500 py-2">Updating cart...</div>}
              {cartItems.length === 0 ? (
                <div className="text-center py-16">
                  <ShoppingBag className="w-20 h-20 mx-auto text-gray-300 mb-6" />
                  <h3 className="text-xl font-semibold mb-2">Your cart is empty</h3>
                  <button onClick={onClose} className="text-blue-600 hover:underline">
                    {continueShoppingText}
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {cartItems.map((item) => (
                    <CartItemRow
                      key={item.product_id}
                      item={item}
                      navigate={navigate}
                      changeQuantity={changeQuantity}
                      removeItem={removeItem}
                      customStyles={customStyles}
                      formatCurrency={formatCurrency}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {cartItems.length > 0 && (
          <div className="p-6 border-t bg-gray-50">
            <div className={`flex justify-between text-xl font-bold mb-6 ${customStyles.textColor}`}>
              <span>Total</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            {syncError && <div className="text-red-600 text-sm mb-4">{syncError}</div>}
            <button
              onClick={() => navigate('/Checkout')}
              className={`w-full py-4 rounded-xl font-semibold hover:opacity-90 transition ${customStyles.buttonBg} ${customStyles.buttonText}`}
            >
              {checkoutText}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderDesign = () => {
    switch (activeDesign) {
      case 'sidebar':
        return <DesignSidebar />;
      case 'bottom':
        return <DesignBottomSheet />;
      case 'center':
        return <DesignCenterModal />;
      default:
        return <DesignSidebar />;
    }
  };

  return <div className="relative z-[9999]">{renderDesign()}</div>;
};

export default CartSection;